import { useState, useEffect, useRef } from "react";
import "./LoadingDemo.css";

// 1. DECRYPT ANIMATION - Random chars become TACITUS
const DecryptLoader = () => {
  const [text, setText] = useState("████████");
  const targetText = "TACITUS";
  const chars = "█▓▒░ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*";
  
  useEffect(() => {
    let iteration = 0;
    const interval = setInterval(() => {
      setText(prev => 
        targetText.split("").map((char, i) => {
          if (i < iteration) return targetText[i];
          return chars[Math.floor(Math.random() * chars.length)];
        }).join("")
      );
      iteration += 1/3;
      if (iteration >= targetText.length) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="loader-container">
      <h1 className="decrypt-text">{text}</h1>
      <p className="loader-subtitle">Decrypting...</p>
    </div>
  );
};

// 2. MATRIX RAIN - SIMPLE VERSION
const MatrixLoader = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // Get actual container dimensions
    const container = canvas.parentElement;
    const width = container.clientWidth || window.innerWidth;
    const height = container.clientHeight || window.innerHeight - 100;
    
    canvas.width = width;
    canvas.height = height;
    
    const katakana = 'アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン';
    const latin = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const nums = '0123456789';
    const alphabet = katakana + latin + nums;
    
    const fontSize = 14;
    const columns = Math.floor(width / fontSize);
    
    // Create multiple drops per column for denser effect
    const streams = [];
    for (let x = 0; x < columns; x++) {
      // Each column has 2-3 streams at different positions
      const numStreams = 2 + Math.floor(Math.random() * 2);
      for (let s = 0; s < numStreams; s++) {
        streams.push({
          x: x,
          y: Math.floor(Math.random() * (height / fontSize)),
          speed: 0.5 + Math.random() * 1,
          length: 15 + Math.floor(Math.random() * 20)
        });
      }
    }
    
    const draw = () => {
      // Very slight fade - keeps more characters visible
      ctx.fillStyle = 'rgba(10, 10, 10, 0.03)';
      ctx.fillRect(0, 0, width, height);
      
      ctx.font = `${fontSize}px monospace`;
      
      streams.forEach(stream => {
        const x = stream.x * fontSize;
        
        // Draw the stream (multiple characters)
        for (let i = 0; i < stream.length; i++) {
          const y = (stream.y - i) * fontSize;
          if (y > 0 && y < height) {
            const text = alphabet.charAt(Math.floor(Math.random() * alphabet.length));
            
            // Brightness based on position in stream
            if (i === 0) {
              ctx.fillStyle = '#FFFFFF'; // Head is white
            } else if (i < 3) {
              ctx.fillStyle = '#FFD700'; // Near head is bright gold
            } else {
              const alpha = Math.max(0.3, 1 - (i / stream.length));
              ctx.fillStyle = `rgba(201, 162, 39, ${alpha})`;
            }
            
            ctx.fillText(text, x, y);
          }
        }
        
        // Move stream down
        stream.y += stream.speed;
        
        // Reset when off screen
        if ((stream.y - stream.length) * fontSize > height) {
          stream.y = -stream.length;
          stream.speed = 0.5 + Math.random() * 1;
        }
      });
    };
    
    const interval = setInterval(draw, 40);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div style={{ 
      position: 'absolute',
      top: '100px',
      left: 0,
      right: 0,
      bottom: 0,
      background: '#0A0A0A',
      overflow: 'hidden'
    }}>
      <canvas 
        ref={canvasRef} 
        style={{ 
          display: 'block',
          width: '100%',
          height: '100%'
        }} 
      />
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        background: 'rgba(10,10,10,0.9)',
        padding: '30px 60px',
        border: '3px solid #C9A227',
        boxShadow: '0 0 30px rgba(201,162,39,0.6), 0 0 60px rgba(201,162,39,0.4)'
      }}>
        <h1 style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: '72px',
          color: '#C9A227',
          textShadow: '0 0 10px #C9A227, 0 0 20px #C9A227, 0 0 40px rgba(201,162,39,0.6)',
          margin: 0,
          letterSpacing: '8px'
        }}>TACITUS</h1>
      </div>
    </div>
  );
};

// 3. TERMINAL BOOT
const TerminalLoader = () => {
  const [visibleLines, setVisibleLines] = useState(0);
  const [showLogo, setShowLogo] = useState(false);
  
  const terminalLines = [
    "> Initializing TACITUS system...",
    "> Loading threat database...",
    "> Connecting to intelligence network...",
    "> Verifying security protocols...",
    "> System ready.",
  ];
  
  useEffect(() => {
    setVisibleLines(0);
    setShowLogo(false);
    
    const lineTimers = terminalLines.map((_, i) => 
      setTimeout(() => setVisibleLines(i + 1), (i + 1) * 500)
    );
    
    const logoTimer = setTimeout(() => setShowLogo(true), terminalLines.length * 500 + 500);
    
    return () => {
      lineTimers.forEach(clearTimeout);
      clearTimeout(logoTimer);
    };
  }, []);
  
  return (
    <div className="loader-container terminal-bg">
      <div className="terminal-window">
        {terminalLines.slice(0, visibleLines).map((text, i) => (
          <p key={i} className="terminal-line">
            {text}
            {i === visibleLines - 1 && !showLogo && <span className="cursor">_</span>}
          </p>
        ))}
        {showLogo && <h1 className="terminal-logo">TACITUS</h1>}
      </div>
    </div>
  );
};

// 4. LIGHT REVEAL
const LightRevealLoader = () => {
  return (
    <div className="loader-container">
      <div className="light-reveal-container">
        <h1 className="light-reveal-text">TACITUS</h1>
        <div className="light-beam" />
      </div>
      <p className="loader-subtitle">Cybersecurity für Entscheider</p>
    </div>
  );
};

// 5. PARTICLE ASSEMBLY
const ParticleAssemblyLoader = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = 600;
    canvas.height = 200;
    
    // Create text path
    ctx.font = "bold 80px 'Cormorant Garamond', serif";
    ctx.fillStyle = "#C9A227";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("TACITUS", 300, 100);
    
    const imageData = ctx.getImageData(0, 0, 600, 200);
    const particles = [];
    
    // Sample pixels
    for (let y = 0; y < 200; y += 4) {
      for (let x = 0; x < 600; x += 4) {
        if (imageData.data[(y * 600 + x) * 4 + 3] > 128) {
          particles.push({
            targetX: x,
            targetY: y,
            x: Math.random() * 600,
            y: Math.random() * 200,
            speed: 0.05 + Math.random() * 0.05
          });
        }
      }
    }
    
    ctx.clearRect(0, 0, 600, 200);
    
    const animate = () => {
      ctx.fillStyle = "rgba(10, 10, 10, 0.2)";
      ctx.fillRect(0, 0, 600, 200);
      
      particles.forEach(p => {
        p.x += (p.targetX - p.x) * p.speed;
        p.y += (p.targetY - p.y) * p.speed;
        ctx.fillStyle = "#C9A227";
        ctx.fillRect(p.x, p.y, 2, 2);
      });
      
      requestAnimationFrame(animate);
    };
    animate();
  }, []);
  
  return (
    <div className="loader-container">
      <canvas ref={canvasRef} className="particle-canvas" />
      <p className="loader-subtitle">Assembling...</p>
    </div>
  );
};

// 6. RADAR SCAN
const RadarLoader = () => {
  return (
    <div className="loader-container">
      <div className="radar-container">
        <div className="radar-circle radar-circle-1" />
        <div className="radar-circle radar-circle-2" />
        <div className="radar-circle radar-circle-3" />
        <div className="radar-sweep" />
        <h1 className="radar-logo">TACITUS</h1>
      </div>
      <p className="loader-subtitle">Scanning threats...</p>
    </div>
  );
};

// 7. ELECTRIC SPARK
const ElectricLoader = () => {
  return (
    <div className="loader-container">
      <div className="electric-container">
        <h1 className="electric-text">TACITUS</h1>
        <div className="spark spark-1" />
        <div className="spark spark-2" />
        <div className="spark spark-3" />
        <div className="spark spark-4" />
      </div>
    </div>
  );
};

// 8. CRT SCANLINES
const CRTLoader = () => {
  return (
    <div className="loader-container crt-container">
      <div className="crt-screen">
        <h1 className="crt-text">TACITUS</h1>
        <div className="scanlines" />
        <div className="crt-flicker" />
      </div>
    </div>
  );
};

// 9. BINARY TO TEXT
const BinaryLoader = () => {
  const [phase, setPhase] = useState(0);
  const binary = "01010100 01000001 01000011 01001001 01010100 01010101 01010011";
  const [displayText, setDisplayText] = useState(binary);
  
  useEffect(() => {
    const steps = [
      { text: binary, delay: 0 },
      { text: "0TACITUS", delay: 500 },
      { text: "TACITUS", delay: 1000 }
    ];
    
    steps.forEach((step, i) => {
      setTimeout(() => {
        setDisplayText(step.text);
        setPhase(i);
      }, step.delay);
    });
  }, []);
  
  return (
    <div className="loader-container">
      <h1 className={`binary-text ${phase === 2 ? 'revealed' : ''}`}>
        {displayText}
      </h1>
      <p className="loader-subtitle">
        {phase < 2 ? "Decoding..." : "System Online"}
      </p>
    </div>
  );
};

// DEMO COMPONENT - Shows all loaders
const LoadingDemo = () => {
  const [currentLoader, setCurrentLoader] = useState(0);
  const loaders = [
    { name: "1. Decrypt Animation", component: DecryptLoader },
    { name: "2. Matrix Rain", component: MatrixLoader },
    { name: "3. Terminal Boot", component: TerminalLoader },
    { name: "4. Light Reveal", component: LightRevealLoader },
    { name: "5. Particle Assembly", component: ParticleAssemblyLoader },
    { name: "6. Radar Scan", component: RadarLoader },
    { name: "7. Electric Spark", component: ElectricLoader },
    { name: "8. CRT Scanlines", component: CRTLoader },
    { name: "9. Binary to Text", component: BinaryLoader },
  ];
  
  const CurrentLoaderComponent = loaders[currentLoader].component;
  
  return (
    <div className="demo-fullscreen">
      <div className="demo-controls">
        <h2>Loading Screen Demo - Wählen Sie Ihren Favoriten</h2>
        <div className="demo-buttons">
          {loaders.map((loader, i) => (
            <button
              key={i}
              onClick={() => setCurrentLoader(i)}
              className={currentLoader === i ? 'active' : ''}
            >
              {loader.name}
            </button>
          ))}
        </div>
      </div>
      <div className="demo-preview" key={currentLoader}>
        <CurrentLoaderComponent />
      </div>
    </div>
  );
};

export default LoadingDemo;
